{
  if (!window.__pdpScrollLock) {
      window.__pdpScrollLock = (() => {
          const allowed = new Set();
          let locks = 0;
          let startY = 0;

          const canScroll = (el) => {
              const oy = getComputedStyle(el).overflowY;
              return (oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight + 1;
          };

          const findScroller = (target) => {
              let node = target;
              while (node && node !== document.body) {
                  if (allowed.has(node)) return node;
                  node = node.parentNode;
              }
              return null;
          };

          const onTouchStart = (e) => {
              if (e.touches.length === 1) startY = e.touches[0].clientY;
          };

          const onTouchMove = (e) => {
              if (e.touches.length > 1) return;
              const scroller = findScroller(e.target);
              if (!scroller || !canScroll(scroller)) {
                  if (e.cancelable) e.preventDefault();
                  return;
              }
              const dy = e.touches[0].clientY - startY;
              const atTop = scroller.scrollTop <= 0;
              const atBottom = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1;
              if ((atTop && dy > 0) || (atBottom && dy < 0)) {
                  if (e.cancelable) e.preventDefault();
              }
          };

          return {
              lock(scrollEl) {
                  if (scrollEl) allowed.add(scrollEl);
                  locks += 1;
                  if (locks === 1) {
                      document.addEventListener('touchstart', onTouchStart, { passive: true });
                      document.addEventListener('touchmove', onTouchMove, { passive: false });
                      document.documentElement.classList.add('pdp-scroll-locked');
                      document.body.classList.add('pdp-scroll-locked');
                  }
              },
              unlock(scrollEl) {
                  if (scrollEl) allowed.delete(scrollEl);
                  locks = Math.max(0, locks - 1);
                  if (locks === 0) {
                      document.removeEventListener('touchstart', onTouchStart, { passive: true });
                      document.removeEventListener('touchmove', onTouchMove, { passive: false });
                      document.documentElement.classList.remove('pdp-scroll-locked');
                      document.body.classList.remove('pdp-scroll-locked');
                      allowed.clear();
                  }
              },
          };
      })();
  }

  class PickerMobileSheet extends HTMLElement {
      constructor() {
          super();
          this._mounted = false;
          this._currentStepIndex = 0;
          this._modalStartIndex = 0;
          this._inlineConsumed = false;
          this._steps = [];
      }

      connectedCallback() {
          if (this._mounted) return;
          this._mounted = true;
          this.discoverSource();
          this.render();
          this.wireGlobalTriggers();
          this.renderInlineStepOne();
      }

      disconnectedCallback() {
          if (this._triggerHandler) {
              document.removeEventListener('click', this._triggerHandler);
          }
      }

      discoverSource() {
          this._source = document.querySelector('variant-picker-steps-metafields');
          if (!this._source) return;
          this._steps = this.buildSteps();
          this._modalStartIndex = 0;
          this._currentStepIndex = 0;
      }

      buildSteps() {
          const steps = [];
          const restructured = this._source?.querySelector('.restructured-variant-picker');
          if (!restructured) return steps;

          const columns = [...restructured.querySelectorAll(':scope > .option-column')]
              .sort((a, b) => Number(a.dataset.level) - Number(b.dataset.level));

          const nativeValue = this.currentNativeVariantValue();

          const selectPrefix = document.querySelector('[id^="I18nSelectPrefix-"]')?.textContent?.trim() || '';

          columns.forEach((col, idx) => {
              const isNative = !!col.querySelector('.pdp-step-tile--native');
              const titleSlot = col.querySelector('.option-column-title, .pdp-step-column__title');
              const ownTileSel = '.options-wrapper > .pdp-step-tile, .options-wrapper > .option-item';
              const variantMatch = t => !nativeValue || !t.dataset.variantConnection || t.dataset.variantConnection === nativeValue;
              const variantOwnTiles = [...col.querySelectorAll(ownTileSel)].filter(variantMatch);
              const directTile = variantOwnTiles.find(t => t.dataset.stepHelpIdDirect);
              const inheritedTile = variantOwnTiles.find(t => t.dataset.stepHelpId);
              const helpId = directTile?.dataset?.stepHelpIdDirect
                  || inheritedTile?.dataset?.stepHelpId
                  || '';
              const tiles = [...col.querySelectorAll('.options-wrapper > .pdp-step-tile, .options-wrapper > .option-item')]
                  .map(t => ({
                      id: t.dataset.optionId || t.dataset.nativeVariantId || '',
                      title: t.dataset.optionTitle || t.textContent?.trim() || '',
                      optionLabel: (t.dataset.optionLabel || '').replace(/:\s*$/, '').trim(),
                      helper: t.dataset.helperText || '',
                      available: t.dataset.bundleProductAvailable !== 'false',
                      notApplicable: t.dataset.notApplicable === 'true',
                      parentId: t.dataset.parentId || null,
                      helpId: t.dataset.stepHelpId || '',
                      variantConnection: t.dataset.variantConnection || '',
                      isNative: t.classList.contains('pdp-step-tile--native'),
                      isLeaf: t.dataset.isLeaf === 'true',
                      isPreorder: t.dataset.bundlePreorder === 'true',
                      nativeVariantId: t.dataset.nativeVariantId || '',
                      nativeVariantValue: t.dataset.nativeVariantValue || '',
                      bundleProduct: t.dataset.bundleProduct || '',
                      imgSrc: t.querySelector('.pdp-step-tile__thumb img, .option-title-image-wrapper img')?.getAttribute('src') || '',
                      drawerContent: this.extractDrawerContent(t),
                  }))
                  .filter(t => {
                      if (t.isNative) return true;
                      if (!nativeValue) return true;
                      if (!t.variantConnection) return true;
                      return t.variantConnection === nativeValue;
                  });

              const tileLabel = tiles.find(t => t.optionLabel)?.optionLabel || '';
              const rawTitle = (isNative ? titleSlot?.textContent?.trim() : tileLabel)
                  || titleSlot?.textContent?.trim()
                  || `Step ${idx + 1}`;
              const step = {
                  kind: isNative ? 'native' : 'bundle',
                  level: Number(col.dataset.level) || idx,
                  title: (isNative && selectPrefix) ? `${selectPrefix} ${rawTitle}` : rawTitle,
                  helpId,
                  tiles,
                  selected: null,
              };

              // Desktop's foldDeepestColumn moves Year tiles into this column's body
              // as .pdp-step-sub-section. Pick them up so the mobile sheet shows the
              // same chain (factory + year in one step).
              const subSectionEl = col.querySelector('.pdp-step-sub-section');
              if (subSectionEl) {
                  const subTiles = [...subSectionEl.querySelectorAll('.pdp-step-tile, .option-item')].map(t => ({
                      id: t.dataset.optionId || '',
                      title: t.dataset.optionTitle || t.textContent?.trim() || '',
                      helper: t.dataset.helperText || '',
                      available: t.dataset.bundleProductAvailable !== 'false',
                      notApplicable: t.dataset.notApplicable === 'true',
                      parentId: t.dataset.parentId || null,
                      isNative: false,
                      isLeaf: t.dataset.isLeaf === 'true',
                      isPreorder: t.dataset.bundlePreorder === 'true',
                      nativeVariantId: '',
                      nativeVariantValue: '',
                      bundleProduct: t.dataset.bundleProduct || '',
                      imgSrc: t.querySelector('.pdp-step-tile__thumb img, .option-title-image-wrapper img')?.getAttribute('src') || '',
                      drawerContent: this.extractDrawerContent(t),
                  }));
                  step.subSection = {
                      heading: subSectionEl.querySelector('.pdp-step-sub-section__heading')?.textContent?.trim() || 'Choose Your Year',
                      tiles: subTiles,
                      helpId: '',
                      selected: null,
                  };
              }

              steps.push(step);
          });

          // Cap the sheet at 3 steps: collapse every trailing column past the
          // third into the third step's sub-section. The sub-section is
          // parent-filtered at render time, so tiles from different branches
          // (factory->year AND drawer install-leaves) coexist and only the set
          // matching the user's pick shows. MERGE into any existing sub-section
          // (e.g. desktop's foldDeepestColumn already folded years in) instead of
          // overwriting it — overwriting is what dead-ended factory->year before.
          while (steps.length >= 4) {
              const last = steps.pop();
              const host = steps[steps.length - 1];
              const extra = (last.tiles || []).concat(last.subSection ? last.subSection.tiles : []);
              if (host.subSection) {
                  host.subSection.tiles = host.subSection.tiles.concat(extra);
              } else {
                  host.subSection = {
                      heading: last.title || 'Choose Your Year',
                      tiles: extra,
                      helpId: last.helpId,
                      selected: null,
                  };
              }
          }

          const kept = steps.filter(s => (s.tiles && s.tiles.length > 0) || (s.subSection && s.subSection.tiles && s.subSection.tiles.length > 0));
          this.propagateAvailability(kept);
          return kept;
      }

      propagateAvailability(steps) {
          // Mirror desktop computeOOS: a parent tile is unavailable when every
          // child is, and "not applicable" (product not offered on this path)
          // when every unavailable child is — so top-level models grey out
          // with the right chip in the mobile sheet too.
          const allTiles = [];
          steps.forEach(s => {
              allTiles.push(...(s.tiles || []));
              if (s.subSection) allTiles.push(...s.subSection.tiles);
          });
          const byParent = new Map();
          allTiles.forEach(t => {
              if (!t.parentId) return;
              if (!byParent.has(t.parentId)) byParent.set(t.parentId, []);
              byParent.get(t.parentId).push(t);
          });
          const availMemo = new Map();
          const naMemo = new Map();
          const effAvailable = (t) => {
              if (availMemo.has(t)) return availMemo.get(t);
              const kids = t.id ? (byParent.get(t.id) || []) : [];
              const result = kids.length === 0 ? t.available : kids.some(effAvailable);
              availMemo.set(t, result);
              return result;
          };
          const effNotApplicable = (t) => {
              if (naMemo.has(t)) return naMemo.get(t);
              const kids = t.id ? (byParent.get(t.id) || []) : [];
              const result = kids.length === 0
                  ? !!t.notApplicable
                  : kids.every(k => effAvailable(k) || effNotApplicable(k));
              naMemo.set(t, result);
              return result;
          };
          allTiles.forEach(t => {
              if (t.isNative) return;
              const avail = effAvailable(t);
              if (!avail) t.notApplicable = effNotApplicable(t);
              t.available = avail;
          });
      }

      extractDrawerContent(tileEl) {
          // Pull the per-option drawer block (iframe + description) emitted by
          // snippet-drawer-content.liquid. Convert iframe src→data-src so the
          // video only loads when its panel becomes visible.
          const src = tileEl.querySelector('.drawer-content-option .drawer-option-content');
          if (!src) return '';
          const clone = src.cloneNode(true);
          clone.querySelectorAll('iframe[src]').forEach(f => {
              f.setAttribute('data-src', f.getAttribute('src'));
              f.removeAttribute('src');
          });
          return clone.outerHTML;
      }

      stepKind(step, tiles) {
          if (step.kind === 'native') return 'native';
          if (step.subSection) return 'factory';
          if (tiles.some(t => t.drawerContent)) return 'connection';
          return 'bundle';
      }

      swapDrawerPanel(activeId) {
          const block = this.querySelector('.pdp-sheet__drawer-content');
          if (!block) return;
          block.querySelectorAll('.pdp-sheet__drawer-panel').forEach(p => p.setAttribute('hidden', ''));
          const target = block.querySelector(`.pdp-sheet__drawer-panel[data-content-for="${this.escapeAttr(activeId)}"]`);
          if (!target) return;
          target.removeAttribute('hidden');
          target.querySelectorAll('iframe[data-src]').forEach(f => {
              f.setAttribute('src', f.getAttribute('data-src'));
              f.removeAttribute('data-src');
          });
      }

      hydrateActiveDrawerPanel() {
          const active = this.querySelector('.pdp-sheet__drawer-panel:not([hidden])');
          if (!active) return;
          active.querySelectorAll('iframe[data-src]').forEach(f => {
              f.setAttribute('src', f.getAttribute('data-src'));
              f.removeAttribute('data-src');
          });
      }

      currentNativeVariantValue() {
          // Same compound-title join as ChangeVariantDrawerContent.getSelectedVariant —
          // must match, or a 2+ option product resolves to a different value on mobile.
          const checked = [...document.querySelectorAll('variant-radios input[type="radio"]:checked')];
          if (checked.length) return checked.map(r => r.value).join(' / ');
          const activeNative = this._source?.querySelector('.pdp-step-tile--native.active');
          return activeNative?.dataset?.nativeVariantValue || '';
      }

      // ---------- rendering ----------

      render() {
          this.innerHTML = `
              <div class="pdp-sheet__backdrop" data-close></div>
              <button type="button" class="pdp-sheet__close" data-close aria-label="Close"></button>
              <div class="pdp-sheet__panel" role="dialog" aria-modal="true" aria-labelledby="pdp-sheet-title">
                  <div class="pdp-sheet__title" id="pdp-sheet-title">${this._steps[this._currentStepIndex]?.title || ''}</div>
                  <div class="pdp-sheet__tabs" role="tablist"></div>
                  <div class="pdp-sheet__body" data-role="body"></div>
                  <div class="pdp-sheet__footer" data-role="footer"></div>
              </div>
          `;
          this.renderTabs();
          this.attachLocalHandlers();
          this.renderBody();
          this.renderFooter();
      }

      // Least-shown-first: a step tab only counts once it's PROVEN reachable.
      // A next step is proven when the current step's selection is a non-leaf
      // with children there, or — while unselected — when every possible choice
      // is a non-leaf (so the next step is guaranteed regardless of pick).
      effectiveStepCount() {
          let count = 1;
          for (let i = 0; i < this._steps.length - 1; i++) {
              const s = this._steps[i];
              const tiles = this.filterTilesForStep(s, i);
              if (s.selected) {
                  const selectedTile = tiles.find(t => t.id === s.selected);
                  if (selectedTile?.isLeaf) break;
                  const next = this._steps[i + 1];
                  const filtered = next.kind === 'native'
                      ? next.tiles
                      : next.tiles.filter(t => !t.parentId || t.parentId === s.selected);
                  if (filtered.length === 0) break;
                  count = i + 2;
              } else {
                  if (tiles.length && tiles.every(t => !t.isLeaf)) count = i + 2;
                  break;
              }
          }
          return count;
      }

      renderTabs() {
          const tabsEl = this.querySelector('.pdp-sheet__tabs');
          if (!tabsEl) return;

          const effective = this.effectiveStepCount();
          const start = this._modalStartIndex;
          const tabs = this._steps.slice(start, effective).map((s, pos) => {
              const i = pos + start;
              const locked = !this.isStepReachable(i) || !this.stepHasContent(i);
              const isActive = i === this._currentStepIndex;
              return `
                  <button
                      type="button"
                      class="tab-selector__tab${isActive ? ' tab-selector__tab--active' : ''}${locked ? ' tab-selector__tab--locked' : ''}"
                      role="tab"
                      data-tab-index="${i}"
                      data-tab-id="step-${i}"
                      aria-selected="${isActive ? 'true' : 'false'}"
                      ${locked ? 'aria-disabled="true"' : ''}
                  >Step ${i - start + 1}</button>
              `;
          }).join('');

          tabsEl.innerHTML = `
              <tab-selector class="tab-selector pdp-sheet__tab-selector">
                  <div class="tab-selector__track" role="tablist">
                      <div class="tab-selector__indicator" aria-hidden="true"></div>
                      ${tabs}
                  </div>
              </tab-selector>
          `;

          const selector = tabsEl.querySelector('tab-selector');
          if (selector && !selector._wired) {
              selector._wired = true;
              selector.addEventListener('tab-change', (e) => {
                  const idx = e.detail.index + this._modalStartIndex;
                  if (!this.isStepReachable(idx) || !this.stepHasContent(idx)) {
                      requestAnimationFrame(() => selector.selectTab(this._currentStepIndex - this._modalStartIndex));
                      return;
                  }
                  this.setStep(idx);
              });
          }
      }

      isStepReachable(targetIndex) {
          if (targetIndex >= this.effectiveStepCount()) return false;
          if (targetIndex <= this._currentStepIndex) return true;
          if (targetIndex !== this._currentStepIndex + 1) return false;
          for (let i = 0; i <= this._currentStepIndex; i++) {
              const s = this._steps[i];
              if (!s) return false;
              if (s.subSection && !s.subSection.selected) return false;
              if (!s.selected) return false;
          }
          return true;
      }

      // ---------- handlers ----------

      attachLocalHandlers() {
          this.addEventListener('click', (e) => {
              if (e.target.closest('[data-close]')) {
                  e.stopPropagation();
                  this.close();
                  return;
              }
              const back = e.target.closest('[data-action="step-back"]');
              if (back) {
                  e.stopPropagation();
                  this.setStep(this._currentStepIndex - 1);
                  return;
              }
              const next = e.target.closest('[data-action="step-next"]');
              if (next) {
                  e.stopPropagation();
                  this.setStep(this._currentStepIndex + 1);
                  return;
              }
              const cont = e.target.closest('[data-action="continue"]');
              if (cont) {
                  e.stopPropagation();
                  this.submitForm();
                  return;
              }
              const helpPill = e.target.closest('.pdp-sheet__help-pill');
              if (helpPill) {
                  e.stopPropagation();
                  const id = helpPill.dataset.stepHelpId;
                  if (id && typeof this._source?.openStepHelp === 'function') {
                      this._source.openStepHelp(id, helpPill);
                  }
                  return;
              }
              const tile = e.target.closest('.pdp-sheet-tile');
              if (tile) {
                  e.stopPropagation();
                  this.selectTile(tile);
                  return;
              }
          });
      }

      selectTile(tileEl) {
          // Not-applicable paths (product not offered) are inert; genuinely
          // sold-out variants stay selectable for the sold-out ATC state.
          if (tileEl.dataset.notApplicable === 'true') return;
          const step = this._steps[this._currentStepIndex];
          if (!step) return;
          const id = tileEl.dataset.tileId;

          // Determine whether the tile is in the main step body or the sub-section.
          const inSubSection = !!tileEl.closest('.pdp-sheet-section');
          if (inSubSection && step.subSection) {
              step.subSection.selected = id;
              this.querySelectorAll('.pdp-sheet-section .pdp-sheet-tile').forEach(t => t.classList.remove('pdp-sheet-tile--active'));
              tileEl.classList.add('pdp-sheet-tile--active');
          } else {
              step.selected = id;
              this.querySelectorAll('.pdp-sheet__body > .pdp-sheet__tiles > .pdp-sheet-tile').forEach(t => t.classList.remove('pdp-sheet-tile--active'));
              tileEl.classList.add('pdp-sheet-tile--active');
              this.swapDrawerPanel(id);
              for (let i = this._currentStepIndex + 1; i < this._steps.length; i++) {
                  this._steps[i].selected = null;
                  if (this._steps[i].subSection) this._steps[i].subSection.selected = null;
              }
              // If selecting a main tile clears sub-section selection that no longer matches,
              // re-render the sub-section so it filters correctly.
              if (step.subSection) {
                  step.subSection.selected = null;
                  this.animatePanelHeight(() => {
                      this.renderBody();
                      // re-mark the tile as active since renderBody wiped state
                      const main = this.querySelector(`.pdp-sheet__body > .pdp-sheet__tiles .pdp-sheet-tile[data-tile-id="${this.escapeAttr(id)}"]`);
                      if (main) main.classList.add('pdp-sheet-tile--active');
                  });
              }
          }

          this.dispatchSelection(tileEl);

          const selector = this.querySelector('tab-selector');
          if (selector) {
              selector.querySelectorAll('.tab-selector__tab').forEach((t, pos) => {
                  const i = pos + this._modalStartIndex;
                  const locked = !this.isStepReachable(i) || !this.stepHasContent(i);
                  t.classList.toggle('tab-selector__tab--locked', locked);
                  if (locked) t.setAttribute('aria-disabled', 'true');
                  else t.removeAttribute('aria-disabled');
              });
          }

          const effective = this.effectiveStepCount();
          if (this._currentStepIndex >= effective) {
              this._currentStepIndex = Math.max(this._modalStartIndex, effective - 1);
          }
          this.renderTabs();
          this.renderFooter();
          this.notifySelectionChanged();

          const idx = this._currentStepIndex;
          const current = this._steps[idx];
          const subTiles = current?.subSection ? this.filterSubSectionTiles(current) : [];
          const subRequired = current?.subSection && subTiles.length > 0;
          const ready = !!current?.selected && (subRequired ? !!current.subSection.selected : true);
          if (ready) {
              const nextIdx = this.findNextNonEmptyStep(idx);
              // Last step renders the explicit "Continue" button — don't auto-submit.
              // Only auto-advance when there's a further step to move to.
              if (nextIdx !== -1) {
                  clearTimeout(this._autoAdvanceT);
                  this._autoAdvanceT = setTimeout(() => this.setStep(nextIdx), 220);
              }
          }
      }

      dispatchSelection(tileEl) {
          const tileObj = this._findTileObjectByEl(tileEl);
          this.syncInstallationVideoBanner(tileEl);

          // Native variant tile → set the matching radio + fire change so legacy
          // ChangeVariantDrawerContent reveals matching bundle tiles + updates downstream UI.
          const nativeValue = tileEl.dataset.nativeVariantValue;
          if (nativeValue) {
              document.querySelectorAll('variant-radios input[type="radio"]').forEach(input => {
                  const isMatch = input.value === nativeValue;
                  input.checked = isMatch;
                  if (isMatch) input.dispatchEvent(new Event('change', { bubbles: true }));
              });
              // Source DOM is rebuilt synchronously; refresh next-step tiles.
              queueMicrotask(() => this.refreshAfterNativeChange());
              this._emitResolved(tileObj);
              return;
          }
          // Bundle leaf tile → re-render the price. Nothing listens for change
          // on the hidden variant input, so the old write left the price on the
          // previous variant while add-to-cart used the new one. Sheet tiles
          // carry no data-bundle-product-url, so refresh via the source tile.
          const bundleVariantId = tileEl.dataset.bundleProduct;
          if (bundleVariantId) {
              this._source?.syncVariantIdInputs?.(bundleVariantId);
              const srcTile = this.findSourceTile(tileEl);
              if (srcTile && typeof this._source?.updateProductInfo === 'function') {
                  this._source.updateProductInfo(srcTile);
              }
          }
          this._emitResolved(tileObj);
      }

      findSourceTile(tileEl) {
          const id = tileEl?.dataset?.tileId;
          if (!id || !this._source) return null;
          const safeId = (window.CSS && CSS.escape) ? CSS.escape(id) : id;
          return this._source.querySelector(`[data-option-id="${safeId}"]`)
              || this._source.querySelector(`[data-native-variant-id="${safeId}"]`);
      }

      syncInstallationVideoBanner(tileEl) {
          if (typeof this._source?.updateInstallationVideoBanner !== 'function') return;
          const srcTile = this.findSourceTile(tileEl);
          if (srcTile) this._source.updateInstallationVideoBanner(srcTile);
      }

      _findTileObjectByEl(tileEl) {
          const id = tileEl?.dataset?.tileId || '';
          if (!id) return null;
          for (const step of this._steps) {
              const t = step.tiles.find(x => x.id === id);
              if (t) return t;
              if (step.subSection) {
                  const st = step.subSection.tiles.find(x => x.id === id);
                  if (st) return st;
              }
          }
          return null;
      }

      _emitResolved(tile) {
          if (tile && tile.isLeaf) {
              document.dispatchEvent(new CustomEvent('bundle:variant-resolved', {
                  detail: {
                      resolved: true,
                      variantId: tile.bundleProduct || '',
                      available: !!tile.available,
                      isPreorder: !!tile.isPreorder,
                  },
              }));
          } else {
              document.dispatchEvent(new CustomEvent('bundle:variant-resolved', { detail: { resolved: false } }));
          }
      }

      resetSelections() {
          if (!Array.isArray(this._steps)) return;
          this._steps.forEach(s => {
              s.selected = null;
              if (s.subSection) s.subSection.selected = null;
          });
          this._currentStepIndex = this._modalStartIndex;
          this._inlineConsumed = false;
          this.renderTabs?.();
          this.renderBody?.();
          this.renderFooter?.();
          this.renderInlineStepOne?.();
          this.notifySelectionChanged?.();
          this.close?.();
      }

      refreshAfterNativeChange() {
          // Re-read source DOM, but preserve currentStepIndex and any selected ids the
          // user has already made (best-effort match by id).
          const oldSelected = this._steps.map(s => s.selected);
          this._steps = this.buildSteps();
          this._modalStartIndex = 0;
          this._steps.forEach((s, i) => {
              if (oldSelected[i] && s.tiles.some(t => t.id === oldSelected[i])) {
                  s.selected = oldSelected[i];
              }
          });
          if (this._currentStepIndex < this._modalStartIndex) this._currentStepIndex = this._modalStartIndex;
          if (this._currentStepIndex >= this._steps.length) this._currentStepIndex = Math.max(this._modalStartIndex, this._steps.length - 1);
          this.renderTabs();
          this.renderBody();
          this.renderFooter();
          this.renderInlineStepOne();
      }

      submitForm() {
          // "Continue" CLOSES the sheet only — does NOT add to cart. PDP's main
          // ATC button submits the form once the chain is complete. Ensure the
          // sub-section leaf's bundle_product is dispatched first so the form
          // input is pointing at the correct variant when the user taps ATC.
          const step = this._steps[this._currentStepIndex];
          if (step?.subSection?.selected) {
              const tileEl = this.querySelector(`.pdp-sheet-section .pdp-sheet-tile[data-tile-id="${this.escapeAttr(step.subSection.selected)}"]`);
              if (tileEl) this.dispatchSelection(tileEl);
          }
          this.close();
      }

      wireGlobalTriggers() {
          this._triggerHandler = (e) => {
              const inlineTile = e.target.closest('[data-role="inline-step-one-tiles"] .pdp-sheet-tile');
              if (inlineTile) {
                  e.preventDefault();
                  if (!inlineTile.classList.contains('pdp-sheet-tile--oos')) this.selectInlineStepOne(inlineTile);
                  return;
              }
              const trigger = e.target.closest('[data-action="open-mobile-sheet"]');
              if (trigger) {
                  e.preventDefault();
                  this.openToFirstIncomplete();
              }
          };
          document.addEventListener('click', this._triggerHandler);
      }

      // ---------- state ----------

      open() {
          this.removeAttribute('hidden');
          this.setAttribute('open', '');
          if (this._locked) return;
          this._locked = true;
          // Smooth-scroll the background to the selection summary, THEN lock once the
          // scroll settles — the lock's overflow:hidden would clip an in-flight scroll.
          const header = document.querySelector('.pdp-selected__header');
          const applyLock = () => {
              if (!this.hasAttribute('open') || !this._locked) return;
              this._lockApplied = true;
              window.__pdpScrollLock?.lock(this.querySelector('.pdp-sheet__body'));
          };
          if (!header || header.offsetParent === null) { applyLock(); return; }
          try { header.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
          catch (e) { header.scrollIntoView(); }
          this.lockAfterScrollSettles(applyLock);
      }

      lockAfterScrollSettles(cb) {
          cancelAnimationFrame(this._settleRAF);
          let last = window.scrollY;
          let still = 0;
          const tick = () => {
              const y = window.scrollY;
              if (Math.abs(y - last) < 1) {
                  if (++still >= 3) { cb(); return; }
              } else {
                  still = 0;
              }
              last = y;
              this._settleRAF = requestAnimationFrame(tick);
          };
          this._settleRAF = requestAnimationFrame(tick);
      }

      close() {
          this.removeAttribute('open');
          this.setAttribute('hidden', '');
          cancelAnimationFrame(this._settleRAF);
          this._locked = false;
          if (this._lockApplied) {
              this._lockApplied = false;
              window.__pdpScrollLock?.unlock(this.querySelector('.pdp-sheet__body'));
          }
      }

      renderInlineStepOne() {
          if (this._inlineConsumed) return;
          const container = document.querySelector('[data-role="inline-step-one-tiles"]');
          const block = document.querySelector('[data-role="inline-step-one"]');
          if (!container || !block) return;
          const native = this._steps[0];
          if (!native || native.kind !== 'native' || !native.tiles.length) {
              // Fallback: product has no native first-step (e.g. default-variant
              // bundle). No inline entry exists, so expose the modal opener instead.
              block.hidden = true;
              document.querySelector('.pdp-selected-wrap')?.setAttribute('data-no-native', 'true');
              return;
          }
          block.hidden = false;
          const titleEl = document.querySelector('[data-role="inline-step-one-title"]');
          if (titleEl) titleEl.textContent = native.title || '';
          container.innerHTML = native.tiles
              .map(t => this.tileMarkup(t, native.selected === t.id))
              .join('');
      }

      consumeInlineStepOne() {
          if (this._inlineConsumed) return Promise.resolve();
          this._inlineConsumed = true;
          const block = document.querySelector('[data-role="inline-step-one"]');
          if (!block || block.hidden) { if (block) block.hidden = true; return Promise.resolve(); }
          const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
          const h = block.scrollHeight;
          if (reduce || !h) { block.hidden = true; return Promise.resolve(); }
          return new Promise((resolve) => {
              block.style.overflow = 'hidden';
              block.style.maxHeight = h + 'px';
              void block.offsetHeight;
              block.style.transition = 'max-height 280ms cubic-bezier(0.22, 1, 0.36, 1), opacity 220ms ease, margin 280ms cubic-bezier(0.22, 1, 0.36, 1)';
              block.style.maxHeight = '0px';
              block.style.opacity = '0';
              block.style.marginBottom = '0px';
              const onEnd = (e) => {
                  if (e.propertyName !== 'max-height') return;
                  block.removeEventListener('transitionend', onEnd);
                  block.hidden = true;
                  block.removeAttribute('style');
                  resolve();
              };
              block.addEventListener('transitionend', onEnd);
          });
      }

      selectInlineStepOne(tileEl) {
          const native = this._steps[0];
          if (!native) return;
          const id = tileEl.dataset.tileId;
          const changed = native.selected !== id;
          native.selected = id;
          for (let i = 1; i < this._steps.length; i++) {
              this._steps[i].selected = null;
              if (this._steps[i].subSection) this._steps[i].subSection.selected = null;
          }
          document.querySelectorAll('[data-role="inline-step-one-tiles"] .pdp-sheet-tile')
              .forEach(t => t.classList.toggle('pdp-sheet-tile--active', t.dataset.tileId === id));
          this.dispatchSelection(tileEl);
          this.notifySelectionChanged();
          this.consumeInlineStepOne().then(() => {
              this.notifySelectionChanged();
              if (changed) {
                  const next = this.findNextNonEmptyStep(0);
                  if (next !== -1) this.openModalAt(next);
              }
          });
      }

      firstIncompleteModalStep() {
          const effective = this.effectiveStepCount();
          for (let i = this._modalStartIndex; i < effective; i++) {
              if (!this.stepHasContent(i)) continue;
              const s = this._steps[i];
              const subTiles = s.subSection ? this.filterSubSectionTiles(s) : [];
              const subRequired = s.subSection && subTiles.length > 0;
              const complete = !!s.selected && (subRequired ? !!s.subSection.selected : true);
              if (!complete) return i;
          }
          for (let i = this._modalStartIndex; i < effective; i++) {
              if (this.stepHasContent(i)) return i;
          }
          return -1;
      }

      openModalAt(index) {
          if (index < this._modalStartIndex || index >= this._steps.length) return;
          this._currentStepIndex = index;
          this.renderTabs();
          this.renderBody();
          this.renderFooter();
          this.open();
          const selector = this.querySelector('tab-selector');
          if (selector) requestAnimationFrame(() => selector.selectTab(index - this._modalStartIndex));
      }

      openToFirstIncomplete() {
          const target = this.firstIncompleteModalStep();
          if (target === -1) return;
          this.openModalAt(target);
      }

      openToFirstStep() {
          for (let i = this._modalStartIndex; i < this._steps.length; i++) {
              if (this.stepHasContent(i)) { this.openModalAt(i); return; }
          }
      }


      notifySelectionChanged() {
          document.dispatchEvent(new CustomEvent('picker:selection-changed'));
      }

      setStep(index) {
          if (index < this._modalStartIndex || index >= this._steps.length) return;
          if (index === this._currentStepIndex) return;
          const body = this.querySelector('.pdp-sheet__body');
          const panel = this.querySelector('.pdp-sheet__panel');
          const apply = () => {
              this._currentStepIndex = index;
              this.renderTabs();
              this.renderBody();
              this.renderFooter();
          };
          if (!body || !panel) { apply(); return; }
          clearTimeout(this._stepFadeT);
          clearTimeout(this._panelHeightT);
          if (this._fadeOutHandler) {
              body.removeEventListener('transitionend', this._fadeOutHandler);
              this._fadeOutHandler = null;
          }

          const fromH = panel.offsetHeight;
          panel.style.height = fromH + 'px';
          body.classList.add('pdp-sheet__body--fading');

          const afterFadeOut = () => {
              apply();
              panel.style.height = 'auto';
              const toH = panel.offsetHeight;
              panel.style.height = fromH + 'px';
              void panel.offsetHeight;
              panel.style.height = toH + 'px';

              body.classList.add('pdp-sheet__body--fading');
              requestAnimationFrame(() => {
                  requestAnimationFrame(() => body.classList.remove('pdp-sheet__body--fading'));
              });

              const cleanup = () => {
                  panel.style.height = '';
                  panel.removeEventListener('transitionend', onPanelEnd);
                  clearTimeout(this._panelHeightT);
              };
              const onPanelEnd = (e) => {
                  if (e.target === panel && e.propertyName === 'height') cleanup();
              };
              panel.addEventListener('transitionend', onPanelEnd);
              this._panelHeightT = setTimeout(cleanup, 500);
          };

          this._fadeOutHandler = (e) => {
              if (e.target !== body || e.propertyName !== 'opacity') return;
              body.removeEventListener('transitionend', this._fadeOutHandler);
              this._fadeOutHandler = null;
              clearTimeout(this._stepFadeT);
              afterFadeOut();
          };
          body.addEventListener('transitionend', this._fadeOutHandler);
          this._stepFadeT = setTimeout(() => {
              if (!this._fadeOutHandler) return;
              body.removeEventListener('transitionend', this._fadeOutHandler);
              this._fadeOutHandler = null;
              afterFadeOut();
          }, 350);
      }

      stepHasContent(idx) {
          const step = this._steps[idx];
          if (!step) return false;
          return this.filterTilesForStep(step, idx).length > 0;
      }

      findNextNonEmptyStep(fromIdx) {
          for (let i = fromIdx + 1; i < this._steps.length; i++) {
              const step = this._steps[i];
              if (!step) continue;
              if (this.filterTilesForStep(step, i).length > 0) return i;
          }
          return -1;
      }

      updateTitle() {
          const titleEl = this.querySelector('#pdp-sheet-title');
          if (titleEl) titleEl.textContent = this.stepTitle(this._currentStepIndex);
      }

      // Title is per-path: a column's tiles span multiple parents (e.g. years for
      // one model, factories for another), so a single static step.title is wrong.
      // Derive it from the tiles actually shown for the current selection — their
      // option_label is accurate for the branch the user is in.
      stepTitle(index) {
          const step = this._steps[index];
          if (!step) return '';
          if (step.kind === 'native') return step.title;
          const label = this.filterTilesForStep(step, index).find(t => t.optionLabel)?.optionLabel;
          return label || step.title;
      }

      animatePanelHeight(mutate) {
          const panel = this.querySelector('.pdp-sheet__panel');
          if (!panel) { mutate(); return; }
          clearTimeout(this._subHeightT);
          const fromH = panel.offsetHeight;
          mutate();
          panel.style.height = 'auto';
          const toH = panel.offsetHeight;
          if (fromH === toH) { panel.style.height = ''; return; }
          panel.style.height = fromH + 'px';
          void panel.offsetHeight;
          panel.style.height = toH + 'px';
          const cleanup = () => {
              panel.style.height = '';
              panel.removeEventListener('transitionend', onEnd);
              clearTimeout(this._subHeightT);
          };
          const onEnd = (e) => {
              if (e.target === panel && e.propertyName === 'height') cleanup();
          };
          panel.addEventListener('transitionend', onEnd);
          this._subHeightT = setTimeout(cleanup, 420);
      }

      renderBody() {
          this.updateTitle();
          const body = this.querySelector('.pdp-sheet__body');
          if (!body) return;

          // Cross-fade: briefly set opacity 0, write new content, then restore on next frame.
          body.classList.add('pdp-sheet__body--fading');

          const step = this._steps[this._currentStepIndex];
          if (!step) {
              body.innerHTML = '';
              requestAnimationFrame(() => body.classList.remove('pdp-sheet__body--fading'));
              return;
          }

          const tiles = this.filterTilesForStep(step, this._currentStepIndex);
          const helpId = tiles.find(t => t.helpId)?.helpId || '';
          const helpPillHtml = helpId ? this.helpPillMarkup(helpId) : '';
          const kind = this.stepKind(step, tiles);

          let subSectionHtml = '';
          if (step.subSection && step.selected) {
              const subTiles = this.filterSubSectionTiles(step);
              if (subTiles.length > 0) {
                  const subHelpHtml = step.subSection.helpId ? this.helpPillMarkup(step.subSection.helpId) : '';
                  subSectionHtml = `
                      <div class="pdp-sheet-section">
                          <div class="pdp-sheet-section__divider"></div>
                          <div class="pdp-sheet-section__heading">${this.escapeText(step.subSection.heading)}</div>
                          ${subHelpHtml}
                          <div class="pdp-sheet__tiles" data-step-kind="factory-sub">
                              ${subTiles.map(t => this.tileMarkup(t, step.subSection.selected === t.id)).join('')}
                          </div>
                      </div>
                  `;
              }
          }

          const hasDrawer = tiles.some(t => t.drawerContent);
          let drawerBlockHtml = '';
          if (hasDrawer) {
              const panels = tiles.map(t => {
                  if (!t.drawerContent) return '';
                  const active = step.selected === t.id;
                  return `<div class="pdp-sheet__drawer-panel" data-content-for="${this.escapeAttr(t.id)}"${active ? '' : ' hidden'}>${t.drawerContent}</div>`;
              }).join('');
              drawerBlockHtml = `<div class="pdp-sheet__drawer-content">${panels}</div>`;
          }

          body.innerHTML = `
              ${helpPillHtml}
              <div class="pdp-sheet__tiles" data-step-kind="${kind}" data-step-level="${step.level}">
                  ${tiles.map(t => this.tileMarkup(t, step.selected === t.id)).join('')}
              </div>
              ${drawerBlockHtml}
              ${subSectionHtml}
          `;

          if (hasDrawer) this.hydrateActiveDrawerPanel();

          requestAnimationFrame(() => body.classList.remove('pdp-sheet__body--fading'));
      }

      filterSubSectionTiles(step) {
          const sub = step.subSection;
          if (!sub) return [];
          // Sub-section tiles must not render until the parent factory tile is selected.
          if (!step.selected) return [];
          return sub.tiles.filter(t => !t.parentId || t.parentId === step.selected);
      }

      renderFooter() {
          const footer = this.querySelector('.pdp-sheet__footer');
          if (!footer) return;
          const index = this._currentStepIndex;
          const step = this._steps[index];
          const isLast = index === this.effectiveStepCount() - 1;
          const mainSelected = !!step?.selected;
          const subTiles = step?.subSection ? this.filterSubSectionTiles(step) : [];
          const subRequired = step?.subSection && subTiles.length > 0;
          const subSelected = subRequired ? !!step.subSection.selected : true;
          const ready = mainSelected && subSelected;
          const primaryBtn = isLast
              ? `<button type="button" class="pdp-sheet__primary" data-action="continue" ${ready ? '' : 'disabled'}>Continue</button>`
              : '';
          footer.innerHTML = `
              <button type="button" class="pdp-sheet__back" data-action="step-back" ${index <= this._modalStartIndex ? 'hidden' : ''}>
                  &lsaquo; Go Back
              </button>
              ${primaryBtn}
          `;
      }

      filterTilesForStep(step, index) {
          if (step.kind === 'native' || index === 0) return step.tiles;
          const prior = this._steps[index - 1];
          if (!prior?.selected) return step.tiles;
          return step.tiles.filter(t => !t.parentId || t.parentId === prior.selected);
      }

      chipLabel(t) {
          if (t.notApplicable) {
              return document.querySelector('[id^="I18nNotAvailable-"]')?.textContent?.trim() || 'Not available';
          }
          return document.querySelector('[id^="I18nOutOfStock-"]')?.textContent?.trim() || 'Out of stock';
      }

      thumbSrc(url) {
          // Tiles are scraped from desktop markup whose images are ~500px;
          // the sheet renders them at most 79x69, so request a CDN size
          // matching the slot (2x for retina) instead of the desktop size.
          if (!url || !/\/cdn\.shopify\.com|\/cdn\/shop\//.test(url)) return url;
          try {
              const u = new URL(url, window.location.origin);
              u.searchParams.set('width', '160');
              return u.toString();
          } catch (e) {
              return url;
          }
      }

      tileMarkup(t, isActive) {
          const thumb = t.imgSrc
              ? `<span class="pdp-sheet-tile__thumb"><img src="${this.escapeAttr(this.thumbSrc(t.imgSrc))}" alt="" width="79" height="69" loading="lazy" decoding="async"></span>`
              : '';
          const helper = t.helper
              ? `<span class="pdp-sheet-tile__helper">${this.escapeText(t.helper)}</span>`
              : '';
          const oosChip = !t.available
              ? `<span class="pdp-sheet-tile__oos-chip">${this.escapeText(this.chipLabel(t))}</span>`
              : '';
          return `
              <button type="button"
                      class="pdp-sheet-tile ${isActive ? 'pdp-sheet-tile--active' : ''} ${t.available ? '' : 'pdp-sheet-tile--oos'}"
                      data-tile-id="${this.escapeAttr(t.id)}"
                      ${t.isNative ? `data-native-variant-id="${this.escapeAttr(t.nativeVariantId)}" data-native-variant-value="${this.escapeAttr(t.nativeVariantValue)}"` : ''}
                      ${t.bundleProduct ? `data-bundle-product="${this.escapeAttr(t.bundleProduct)}" data-bundle-product-available="${t.available ? 'true' : 'false'}"` : ''}
                      ${t.notApplicable ? 'data-not-applicable="true"' : ''}
                      ${t.isLeaf ? 'data-is-leaf="true"' : ''}>
                  ${oosChip}
                  ${thumb}
                  <span class="pdp-sheet-tile__text">
                      <span class="pdp-sheet-tile__title">${this.escapeText(t.title)}</span>
                      ${helper}
                  </span>
              </button>
          `;
      }

      helpPillMarkup(stepHelpId) {
          // Re-use existing template emitted by snippet-step-help-pill.liquid; tap copies the
          // title text for the pill label.
          const template = this._source?.querySelector(`template.step-help-template[data-step-help-id="${stepHelpId}"]`);
          const drawerTitle = template?.content.querySelector('.step-help-drawer__title')?.textContent?.trim() || 'More info';
          return `
              <button type="button" class="pdp-sheet__help-pill" data-step-help-id="${this.escapeAttr(stepHelpId)}">
                  <span class="pdp-sheet__help-pill__icon" aria-hidden="true">i</span>
                  <span class="pdp-sheet__help-pill__label">${this.escapeText(drawerTitle)}</span>
              </button>
          `;
      }

      escapeText(s) {
          return String(s ?? '').replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
      }
      escapeAttr(s) {
          return String(s ?? '').replace(/["&]/g, c => ({ '"': '&quot;', '&': '&amp;' }[c]));
      }
  }

  if (!customElements.get('picker-mobile-sheet')) {
      customElements.define('picker-mobile-sheet', PickerMobileSheet);
  }

  class PdpSelectedSummary {
      constructor(wrap) {
          this.wrap = wrap;
          this.root = wrap.querySelector('.pdp-selected');
          this.rowsEl = wrap.querySelector('[data-role="selected-rows"]');
          this.headerEl = wrap.querySelector('[data-action="toggle-selected"]');
          if (this.headerEl) {
              this.headerEl.addEventListener('click', () => this.toggle());
          }
          this.refresh();
          this.observeSheetClose();
          document.addEventListener('picker:selection-changed', () => this.refresh());
      }

      toggle() {
          if (!this.root) return;
          const open = this.root.dataset.state === 'open';
          this.root.dataset.state = open ? 'closed' : 'open';
          this.headerEl?.setAttribute('aria-expanded', open ? 'false' : 'true');
      }

      observeSheetClose() {
          const sheet = document.querySelector('picker-mobile-sheet');
          if (!sheet) return;
          new MutationObserver(() => this.refresh()).observe(sheet, { attributes: true, attributeFilter: ['open', 'hidden'] });
      }

      refresh() {
          if (!this.rowsEl) return;
          const sheet = document.querySelector('picker-mobile-sheet');
          const steps = sheet?._steps;
          if (!steps?.length) {
              this.rowsEl.innerHTML = '';
              this.wrap.dataset.hasSelection = 'false';
              return;
          }

          const rows = [];
          steps.forEach((step) => {
              const tileId = step.selected;
              if (!tileId) return;
              const tile = step.tiles.find(t => t.id === tileId);
              if (!tile) return;
              const thumb = tile.imgSrc
                  ? `<span class="pdp-selected__thumb"><img src="${this.escapeAttr(tile.imgSrc)}" alt="" loading="lazy"></span>`
                  : '';
              rows.push(`
                  <div class="pdp-selected__row">
                      <span class="pdp-selected__check" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" width="15" height="11" viewBox="0 0 15 11" fill="none"><path d="M14.7863 0.937511L14.0917 0.221286C13.8056 -0.073762 13.3384 -0.073762 13.0498 0.221286L5.76645 7.7341L1.9511 3.79991C1.66497 3.50482 1.19782 3.50482 0.909221 3.79991L0.214601 4.51613C-0.0715337 4.81121 -0.0715337 5.29288 0.214601 5.59047L5.24306 10.7755C5.38737 10.9243 5.57322 11 5.76156 11C5.94989 11 6.1382 10.9243 6.28008 10.7755L14.779 2.01188C15.0724 1.71179 15.0724 1.23262 14.7863 0.937511Z" fill="white"/></svg></span>
                      ${thumb}
                      <span class="pdp-selected__value">${this.escapeText(tile.title)}</span>
                  </div>
              `);
              if (step.subSection?.selected) {
                  const subTile = step.subSection.tiles.find(t => t.id === step.subSection.selected);
                  if (subTile) {
                      const subThumb = subTile.imgSrc
                          ? `<span class="pdp-selected__thumb"><img src="${this.escapeAttr(subTile.imgSrc)}" alt="" loading="lazy"></span>`
                          : '';
                      rows.push(`
                          <div class="pdp-selected__row">
                              <span class="pdp-selected__check" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" width="15" height="11" viewBox="0 0 15 11" fill="none"><path d="M14.7863 0.937511L14.0917 0.221286C13.8056 -0.073762 13.3384 -0.073762 13.0498 0.221286L5.76645 7.7341L1.9511 3.79991C1.66497 3.50482 1.19782 3.50482 0.909221 3.79991L0.214601 4.51613C-0.0715337 4.81121 -0.0715337 5.29288 0.214601 5.59047L5.24306 10.7755C5.38737 10.9243 5.57322 11 5.76156 11C5.94989 11 6.1382 10.9243 6.28008 10.7755L14.779 2.01188C15.0724 1.71179 15.0724 1.23262 14.7863 0.937511Z" fill="white"/></svg></span>
                              ${subThumb}
                              <span class="pdp-selected__value">${this.escapeText(subTile.title)}</span>
                          </div>
                      `);
                  }
              }
          });
          this.rowsEl.innerHTML = rows.join('');
          this.wrap.dataset.hasSelection = rows.length > 0 ? 'true' : 'false';
      }

      escapeText(s) {
          return String(s ?? '').replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
      }
      escapeAttr(s) {
          return String(s ?? '').replace(/["&]/g, c => ({ '"': '&quot;', '&': '&amp;' }[c]));
      }
  }

  function mountSelectedSummary() {
      const wrap = document.querySelector('.pdp-selected-wrap');
      if (wrap && !wrap._mounted) {
          wrap._mounted = true;
          new PdpSelectedSummary(wrap);
      }
  }
  if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', mountSelectedSummary);
  } else {
      mountSelectedSummary();
  }
}
